// EPSE Synthetic Profile Generator
// Generates realistic energy profiles from limited input data

import type { SyntheticProfileConfig } from '@/types/epse';

export interface SyntheticQuarterData {
  quarter: number;       // 0-95 (quarter of day)
  kw_value: number;
  hour: number;
  isNight: boolean;
  isWeekend: boolean;
  season: 'winter' | 'lente' | 'zomer' | 'herfst';
}

export interface ProfileMethodology {
  steps: MethodologyStep[];
  warnings: string[];
  assumptions: string[];
}

export interface MethodologyStep {
  title: string;
  description: string;
  calculation: string;
  result: string;
}

/**
 * Generate a 96-point daily load profile from synthetic config
 * Uses peak_periods array for multiple customizable peak moments
 */
export function generateDailyProfile(
  config: SyntheticProfileConfig,
  season: 'winter' | 'lente' | 'zomer' | 'herfst',
  isWeekend: boolean = false
): number[] {
  const {
    annual_kwh,
    day_night_ratio,
    season_distribution,
    peak_periods,
    base_load_ratio,
    weekend_reduction,
    connection_kw_limit,
  } = config;

  // Calculate seasonal kWh
  const seasonFactor = season_distribution[season];
  const seasonalKwh = annual_kwh * seasonFactor;
  
  // Days per season (approximate)
  const daysPerSeason = 91;
  const dailyKwh = seasonalKwh / daysPerSeason;
  
  // Apply weekend reduction
  const adjustedDailyKwh = isWeekend ? dailyKwh * (1 - weekend_reduction) : dailyKwh;
  
  // Split into day and night
  const dayKwh = adjustedDailyKwh * day_night_ratio;
  const nightKwh = adjustedDailyKwh * (1 - day_night_ratio);
  
  // Night hours (22:00-06:00 = 32 quarters)
  const nightQuarters = 32;
  // Day hours (06:00-22:00 = 64 quarters)
  const dayQuarters = 64;
  
  // Calculate base and peak kW
  const nightBaseKw = (nightKwh / (nightQuarters * 0.25));
  const dayMeanKw = (dayKwh / (dayQuarters * 0.25));
  
  // Peak calculation with realistic load factor
  const loadFactor = 0.6; // Typical industrial load factor
  const peakKw = Math.min(dayMeanKw / loadFactor, connection_kw_limit * 0.95);
  const baseKw = peakKw * base_load_ratio;
  
  const profile: number[] = [];
  
  for (let q = 0; q < 96; q++) {
    const hour = q / 4; // Decimal hour (0.0 - 23.75)
    const hourInt = Math.floor(hour);
    
    let kw: number;
    
    if (hourInt >= 22 || hourInt < 6) {
      // Night period - constant base load with small variation
      const variation = 1 + (Math.sin(q * 0.3) * 0.05);
      kw = nightBaseKw * variation;
    } else {
      // Day period - start with base load
      kw = baseKw;
      
      // Morning ramp-up (06:00-07:00)
      if (hour >= 6 && hour < 7) {
        const rampProgress = hour - 6;
        const rampFactor = 0.5 * (1 - Math.cos(rampProgress * Math.PI));
        kw = baseKw * 0.5 + baseKw * 0.5 * rampFactor;
      }
      
      // Apply each peak period with Gaussian shape
      for (const peak of peak_periods) {
        if (hour >= peak.start_hour - 0.5 && hour <= peak.end_hour + 0.5) {
          const midpoint = (peak.start_hour + peak.end_hour) / 2;
          const width = Math.max((peak.end_hour - peak.start_hour) / 2, 0.25);
          const gaussian = Math.exp(-Math.pow((hour - midpoint) / width, 2) * 2);
          
          // Add peak contribution
          kw += (peakKw - baseKw) * gaussian * peak.intensity;
        }
      }
      
      // Evening ramp-down (after last peak or 18:00)
      const lastPeakEnd = peak_periods.length > 0 
        ? Math.max(...peak_periods.map(p => p.end_hour))
        : 18;
      
      if (hour >= lastPeakEnd && hour < 22) {
        const rampProgress = (hour - lastPeakEnd) / (22 - lastPeakEnd);
        const rampFactor = 1 - rampProgress;
        // Gradually reduce to night base load
        const targetKw = nightBaseKw;
        kw = targetKw + (kw - targetKw) * rampFactor;
      }
    }
    
    // Add realistic noise (±5%)
    const noise = 1 + (Math.random() - 0.5) * 0.1;
    kw = Math.max(0, kw * noise);
    
    profile.push(kw);
  }
  
  // Scale profile to match daily kWh target
  const currentKwh = profile.reduce((sum, kw) => sum + kw * 0.25, 0);
  const scaleFactor = currentKwh > 0 ? adjustedDailyKwh / currentKwh : 1;
  
  return profile.map(kw => Math.round(kw * scaleFactor * 10) / 10);
}

/**
 * Generate full year of synthetic quarter data (35,040 points)
 */
export function generateYearProfile(config: SyntheticProfileConfig): SyntheticQuarterData[] {
  const data: SyntheticQuarterData[] = [];
  const startDate = new Date('2024-01-01T00:00:00');
  
  // Generate profiles for each season/day-type combination
  const profileCache = new Map<string, number[]>();
  
  for (let day = 0; day < 365; day++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + day);
    
    const month = date.getMonth();
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    // Determine season
    let season: 'winter' | 'lente' | 'zomer' | 'herfst';
    if (month >= 2 && month <= 4) season = 'lente';
    else if (month >= 5 && month <= 7) season = 'zomer';
    else if (month >= 8 && month <= 10) season = 'herfst';
    else season = 'winter';
    
    const cacheKey = `${season}-${isWeekend}`;
    
    if (!profileCache.has(cacheKey)) {
      profileCache.set(cacheKey, generateDailyProfile(config, season, isWeekend));
    }
    
    const dailyProfile = profileCache.get(cacheKey)!;
    
    // Add variation per day (±10%)
    const dayVariation = 0.9 + Math.random() * 0.2;
    
    for (let q = 0; q < 96; q++) {
      const hour = Math.floor(q / 4);
      
      data.push({
        quarter: q,
        kw_value: Math.round(dailyProfile[q] * dayVariation * 10) / 10,
        hour,
        isNight: hour >= 22 || hour < 6,
        isWeekend,
        season,
      });
    }
  }
  
  return data;
}

/**
 * Generate a representative week profile (672 quarters = 7 days)
 */
export function generateWeekProfile(config: SyntheticProfileConfig): SyntheticQuarterData[] {
  const data: SyntheticQuarterData[] = [];
  
  // Generate one representative week
  const days = [
    { isWeekend: false, season: 'winter' as const },
    { isWeekend: false, season: 'winter' as const },
    { isWeekend: false, season: 'winter' as const },
    { isWeekend: false, season: 'winter' as const },
    { isWeekend: false, season: 'winter' as const },
    { isWeekend: true, season: 'winter' as const },
    { isWeekend: true, season: 'winter' as const },
  ];
  
  days.forEach((dayConfig, dayIndex) => {
    const profile = generateDailyProfile(config, dayConfig.season, dayConfig.isWeekend);
    
    profile.forEach((kw, q) => {
      const hour = Math.floor(q / 4);
      data.push({
        quarter: q,
        kw_value: kw,
        hour,
        isNight: hour >= 22 || hour < 6,
        isWeekend: dayConfig.isWeekend,
        season: dayConfig.season,
      });
    });
  });
  
  return data;
}

/**
 * Get default synthetic config with peak periods
 */
export function getDefaultSyntheticConfig(annualKwh: number = 500000, connectionLimit: number = 150): SyntheticProfileConfig {
  // Calculate a sensible default peak based on annual usage
  const avgDailyKwh = annualKwh / 365;
  const estimatedPeak = Math.min((avgDailyKwh / 24) / 0.6, connectionLimit * 1.2);
  
  return {
    annual_kwh: annualKwh,
    connection_kw_limit: connectionLimit,
    day_night_ratio: 0.75,
    season_distribution: {
      winter: 0.30,
      lente: 0.22,
      zomer: 0.20,
      herfst: 0.28,
    },
    peak_periods: [
      { id: '1', start_hour: 7, end_hour: 9, intensity: 1.0, label: 'Ochtendpiek' },
      { id: '2', start_hour: 12, end_hour: 13, intensity: 0.7, label: 'Lunchpiek' },
      { id: '3', start_hour: 16, end_hour: 18, intensity: 0.9, label: 'Eindedagpiek' },
    ],
    base_load_ratio: 0.25,
    weekend_reduction: 0.40,
    // New fields for manual peak and PV
    peak_kw_manual: Math.round(estimatedPeak),
    use_manual_peak: false,
    pv_capacity_kwp: 0,
    pv_profile_type: 'none',
  };
}

/**
 * Generate methodology explanation
 */
export function generateMethodology(config: SyntheticProfileConfig): ProfileMethodology {
  const steps: MethodologyStep[] = [];
  const warnings: string[] = [];
  const assumptions: string[] = [];
  
  // Step 1: Annual distribution
  const winterKwh = Math.round(config.annual_kwh * config.season_distribution.winter);
  const lenteKwh = Math.round(config.annual_kwh * config.season_distribution.lente);
  const zomerKwh = Math.round(config.annual_kwh * config.season_distribution.zomer);
  const herfstKwh = Math.round(config.annual_kwh * config.season_distribution.herfst);
  
  steps.push({
    title: '1. Seizoensverdeling',
    description: 'Jaarverbruik verdelen over de seizoenen op basis van opgegeven percentages.',
    calculation: `${config.annual_kwh.toLocaleString('nl-NL')} kWh × seizoenspercentages`,
    result: `Winter: ${winterKwh.toLocaleString('nl-NL')} kWh | Lente: ${lenteKwh.toLocaleString('nl-NL')} kWh | Zomer: ${zomerKwh.toLocaleString('nl-NL')} kWh | Herfst: ${herfstKwh.toLocaleString('nl-NL')} kWh`,
  });
  
  // Step 2: Day/Night split
  const winterDayKwh = Math.round(winterKwh * config.day_night_ratio);
  const winterNightKwh = Math.round(winterKwh * (1 - config.day_night_ratio));
  
  steps.push({
    title: '2. Dag/Nacht Verdeling',
    description: 'Per seizoen splitsen in dag- en nachtverbruik.',
    calculation: `Dag = ${(config.day_night_ratio * 100).toFixed(0)}% | Nacht = ${((1 - config.day_night_ratio) * 100).toFixed(0)}%`,
    result: `Voorbeeld winter: Dag ${winterDayKwh.toLocaleString('nl-NL')} kWh | Nacht ${winterNightKwh.toLocaleString('nl-NL')} kWh`,
  });
  
  // Step 3: Load profile shape
  const dailyKwh = winterKwh / 91;
  const meanKw = dailyKwh / 24;
  const estimatedPeak = Math.min(meanKw / 0.6, config.connection_kw_limit * 0.95);
  const baseKw = estimatedPeak * config.base_load_ratio;
  
  steps.push({
    title: '3. Belastingsprofiel',
    description: 'Dagelijks profiel opbouwen met meerdere piekmomenten en Gaussian pieken.',
    calculation: `Geschat piekvermogen = Gemiddeld kW / Load Factor (0.6) = ${meanKw.toFixed(1)} / 0.6 ≈ ${estimatedPeak.toFixed(0)} kW`,
    result: `Piekvermogen: ${estimatedPeak.toFixed(0)} kW | Basisbelasting: ${baseKw.toFixed(0)} kW (${(config.base_load_ratio * 100).toFixed(0)}%)`,
  });
  
  // Step 4: Peak periods
  const peakPeriodsDesc = config.peak_periods.length > 0
    ? config.peak_periods.map(p => `${p.label || 'Piek'}: ${p.start_hour}:00-${p.end_hour}:00 (${(p.intensity * 100).toFixed(0)}%)`).join(' | ')
    : 'Geen piekmomenten gedefinieerd';
  
  steps.push({
    title: '4. Piekmomenten',
    description: 'Elk piekmoment wordt toegepast als Gaussian curve met instelbare intensiteit.',
    calculation: 'kW = Basis + (Piek - Basis) × Gaussian(uur) × Intensiteit',
    result: peakPeriodsDesc,
  });
  
  // Step 5: Scaling
  steps.push({
    title: '5. Schaling naar jaarverbruik',
    description: 'Profiel schalen zodat het totaal exact overeenkomt met opgegeven jaarverbruik.',
    calculation: 'Schaalfactor = Doel kWh / Berekend kWh',
    result: `Gegarandeerd totaal: ${config.annual_kwh.toLocaleString('nl-NL')} kWh/jaar`,
  });
  
  // Assumptions
  assumptions.push('Load factor van 0.6 voor industrieel profiel');
  assumptions.push(`${config.peak_periods.length} piekmomenten gedefinieerd`);
  assumptions.push(`Weekendreductie: ${(config.weekend_reduction * 100).toFixed(0)}%`);
  assumptions.push('Nachturen (22:00-06:00) hebben constante basisbelasting');
  assumptions.push('Realistische dagelijkse variatie van ±10%');
  
  // Warnings
  const totalSeasonPct = Object.values(config.season_distribution).reduce((a, b) => a + b, 0);
  if (Math.abs(totalSeasonPct - 1) > 0.01) {
    warnings.push(`Seizoenspercentages optellen tot ${(totalSeasonPct * 100).toFixed(0)}% (zou 100% moeten zijn)`);
  }
  
  if (estimatedPeak > config.connection_kw_limit) {
    warnings.push(`Geschat piekvermogen (${estimatedPeak.toFixed(0)} kW) overschrijdt contractgrens (${config.connection_kw_limit} kW)`);
  }
  
  const avgDailyKwh = config.annual_kwh / 365;
  const minPossiblePeak = avgDailyKwh / 24;
  if (minPossiblePeak > config.connection_kw_limit * 0.9) {
    warnings.push('Jaarverbruik is zeer hoog t.o.v. contractgrens - mogelijk onrealistisch profiel');
  }
  
  return { steps, warnings, assumptions };
}
